import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import Header from './Header';
import Footer from './Footer';

const AdminDashboard = () => {
  const { isDarkMode } = useTheme();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all');
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Theme classes
  const bgClass = isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]';
  const cardBgClass = isDarkMode ? 'bg-[#111111]' : 'bg-white';
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#888888]' : 'text-[#666666]';
  const borderClass = isDarkMode ? 'border-white/10' : 'border-[#E5E5E5]';
  const inputBgClass = isDarkMode ? 'bg-[#1A1A1A]' : 'bg-white';

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        fetchSubmissions(token);
      } else {
        localStorage.removeItem('adminToken');
        setCheckingAuth(false);
      }
    } catch (err) {
      localStorage.removeItem('adminToken');
      setCheckingAuth(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
        fetchSubmissions(data.token);
      } else {
        setAuthError(data.detail || 'Invalid password');
      }
    } catch (err) {
      setAuthError('Connection error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setSubmissions([]);
    setSelectedSubmission(null);
  };

  const fetchSubmissions = async (token = null) => {
    const authToken = token || localStorage.getItem('adminToken');
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch submissions');
      const data = await response.json();
      setSubmissions(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setCheckingAuth(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contact/${id}/status?status=${newStatus}`,
        { 
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to update status');
      
      // Update local state
      setSubmissions(prev =>
        prev.map(sub =>
          sub.id === id ? { ...sub, status: newStatus } : sub
        )
      );
      
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-[#F6CD17] text-black';
      case 'read': return 'bg-blue-500 text-white';
      case 'responded': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <>
        <Header />
        <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F6CD17]"></div>
        </div>
      </>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Header />
        <div className={`min-h-screen ${bgClass} flex items-center justify-center px-4`}>
          <div className={`w-full max-w-md ${cardBgClass} rounded-2xl p-8 border ${borderClass}`}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#F6CD17]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#F6CD17]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className={`font-display text-2xl ${textClass} mb-2`}>Admin Access</h1>
              <p className={subtextClass}>Enter password to view submissions</p>
            </div>
            
            <form onSubmit={handleLogin}>
              {authError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                  {authError}
                </div>
              )}
              
              <div className="mb-6">
                <label className={`block text-sm font-medium ${subtextClass} mb-2`}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className={`w-full px-4 py-3 rounded-lg border ${borderClass} ${inputBgClass} ${textClass} focus:outline-none focus:ring-2 focus:ring-[#F6CD17]/50`}
                  required
                  autoFocus
                />
              </div>
              
              <button
                type="submit"
                disabled={authLoading}
                className="w-full py-3 bg-[#F6CD17] text-black font-medium rounded-lg hover:bg-[#F6CD17]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Access Dashboard'
                )}
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <>
        <Header />
        <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F6CD17]"></div>
        </div>
      </>
    );
  }

  // Main dashboard view
  return (
    <>
      <Header />
      <div className={`min-h-screen ${bgClass} pt-24 pb-12 px-4 md:px-8`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className={`font-display text-3xl md:text-4xl ${textClass} mb-2`}>
                Contact Submissions
              </h1>
              <p className={subtextClass}>
                {submissions.length} total submission{submissions.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className={`px-4 py-2 border ${borderClass} rounded-lg ${textClass} hover:bg-white/5 transition-colors flex items-center gap-2`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500">
              {error}
            </div>
          )}

          {/* Filter Tabs */}
          <div className={`flex gap-2 mb-6 p-1 ${cardBgClass} rounded-lg w-fit`}>
            {['all', 'new', 'read', 'responded'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  filter === f
                    ? 'bg-[#F6CD17] text-black'
                    : `${subtextClass} hover:${textClass}`
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f !== 'all' && (
                  <span className="ml-2 opacity-70">
                    ({submissions.filter(s => s.status === f).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Submissions List */}
            <div className={`lg:col-span-1 ${cardBgClass} rounded-xl border ${borderClass} overflow-hidden`}>
              <div className={`p-4 border-b ${borderClass}`}>
                <h2 className={`font-medium ${textClass}`}>Submissions</h2>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {filteredSubmissions.length === 0 ? (
                  <div className={`p-8 text-center ${subtextClass}`}>
                    No submissions found
                  </div>
                ) : (
                  filteredSubmissions.map((submission) => (
                    <div
                      key={submission.id}
                      onClick={() => {
                        setSelectedSubmission(submission);
                        if (submission.status === 'new') {
                          updateStatus(submission.id, 'read');
                        }
                      }}
                      className={`p-4 border-b ${borderClass} cursor-pointer transition-all hover:bg-white/5 ${
                        selectedSubmission?.id === submission.id ? 'bg-white/10' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className={`font-medium ${textClass} truncate pr-2`}>
                          {submission.name}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(submission.status)}`}>
                          {submission.status}
                        </span>
                      </div>
                      <p className={`text-sm ${subtextClass} truncate`}>
                        {submission.email}
                      </p>
                      <p className={`text-xs ${subtextClass} mt-1`}>
                        {formatDate(submission.submitted_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Submission Detail */}
            <div className={`lg:col-span-2 ${cardBgClass} rounded-xl border ${borderClass}`}>
              {selectedSubmission ? (
                <div className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className={`font-display text-2xl ${textClass} mb-1`}>
                        {selectedSubmission.name}
                      </h2>
                      <a 
                        href={`mailto:${selectedSubmission.email}`}
                        className="text-[#F6CD17] hover:underline"
                      >
                        {selectedSubmission.email}
                      </a>
                    </div>
                    <select
                      value={selectedSubmission.status}
                      onChange={(e) => updateStatus(selectedSubmission.id, e.target.value)}
                      className={`px-3 py-2 rounded-lg border ${borderClass} ${cardBgClass} ${textClass} text-sm`}
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="responded">Responded</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    {selectedSubmission.company && (
                      <div>
                        <label className={`text-xs uppercase tracking-wider ${subtextClass}`}>
                          Company
                        </label>
                        <p className={textClass}>{selectedSubmission.company}</p>
                      </div>
                    )}

                    <div>
                      <label className={`text-xs uppercase tracking-wider ${subtextClass}`}>
                        Message
                      </label>
                      <p className={`${textClass} whitespace-pre-wrap mt-1 leading-relaxed`}>
                        {selectedSubmission.message}
                      </p>
                    </div>

                    <div>
                      <label className={`text-xs uppercase tracking-wider ${subtextClass}`}>
                        Submitted
                      </label>
                      <p className={subtextClass}>
                        {formatDate(selectedSubmission.submitted_at)}
                      </p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className={`mt-8 pt-6 border-t ${borderClass} flex gap-3`}>
                    <a
                      href={`mailto:${selectedSubmission.email}?subject=Re: Your inquiry to FLINCH`}
                      className="px-4 py-2 bg-[#F6CD17] text-black rounded-lg font-medium hover:bg-[#F6CD17]/90 transition-colors"
                      onClick={() => updateStatus(selectedSubmission.id, 'responded')}
                    >
                      Reply via Email
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedSubmission.email);
                      }}
                      className={`px-4 py-2 border ${borderClass} rounded-lg ${textClass} hover:bg-white/5 transition-colors`}
                    >
                      Copy Email
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`h-full flex items-center justify-center p-12 ${subtextClass}`}>
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p>Select a submission to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Refresh Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => fetchSubmissions()}
              className={`px-4 py-2 border ${borderClass} rounded-lg ${textClass} hover:bg-white/5 transition-colors flex items-center gap-2`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminDashboard;
